import { useGroupInvites } from '@echo/core/react';
import { Button } from '@echo/ui-web';
import { plural } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ToolbarBack } from '@/components/empty-state-frame';
import { PendingInvites } from '@/components/pending-invites';

interface SentSearch {
  sent: number;
}

import { useGoBack } from '@/lib/use-go-back';

export const Route = createFileRoute('/_app/account_/groups_/$id_/invite_/sent')({
  validateSearch: (search: Record<string, unknown>): SentSearch => {
    const sent = Number(search.sent);
    return { sent: Number.isInteger(sent) && sent > 0 ? sent : 0 };
  },
  component: InvitesSentPage,
});

/**
 * Invite people › sent (Figma Invite, confirmation): how many invites went, and the group's
 * pending invites — address, the access they will have, when they were invited. "Back to
 * group" returns to the group's page the invite started from (Group profile or Manage group).
 */
function InvitesSentPage() {
  const { t } = useLingui();
  const navigate = useNavigate();
  const goBack = useGoBack(
    () => void navigate({ to: '/account/groups/$id', params: { id }, replace: true }),
  );
  const { id } = Route.useParams();
  const { sent: count } = Route.useSearch();
  const invites = useGroupInvites(id).data ?? [];
  return (
    <>
      <ToolbarBack
        render={({ className, children }) => (
          <button type="button" className={className} onClick={goBack}>
            {children}
          </button>
        )}
      />
      <h1 className="text-display-sm font-bold">
        <Trans>Invite people</Trans>
      </h1>
      <div className="flex flex-col gap-section-sm pt-content-md">
        <section className="flex flex-col gap-internal-sm rounded-xl border border-stat-card-border bg-leader-card p-content-md">
          <h2 className="text-title-md font-bold">
            {plural(count, { one: '# invite sent', other: '# invites sent' })}
          </h2>
          <p className="text-body-md">
            <Trans>You'll see them in the group once they accept the invitation to join.</Trans>
          </p>
        </section>
        <PendingInvites invites={invites} />
        <div className="flex flex-wrap justify-end gap-content-sm">
          <Button onClick={goBack}>{t`Back to group`}</Button>
        </div>
      </div>
    </>
  );
}
